import React, { useEffect, useMemo } from 'react'
import { Button, StatsCard, Table } from '@demandlocal/components'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import PolarAreaChart from '../../../../components/Charts/PolarAreaChart'
import HorizontalBarChart from '../../../../components/Charts/HorizontalBarChart'
import { take } from 'lodash'
import { useGetInventoryAnalysis } from '../../../../api/dealership/inventory/useGetInventoryAnalysis'
import StackedBarChart from '../../../../components/Charts/StackedBarChart'
import { createColumnHelper } from '@tanstack/react-table'
import { Vehicle } from '../../../../lib/types/Vehicle'
import { useGetAddedInventory } from '../../../../api/dealership/inventory/useGetAddedInventory'
import { useGetRemovedInventory } from '../../../../api/dealership/inventory/useGetRemovedInventory'
import { GA4 } from '../../../../lib/utils/ga4'
import mtm from '../../../../lib/utils/mtm'
import { useTranslation } from 'react-i18next'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import { useGetInventoryBreakdownStats } from '../../../../api/dealership/inventory/analysis/useGetInventoryBreakdownStats'
import { useGetInventoryAlerts } from '../../../../api/dealership/inventory/analysis/useGetInventoryAlerts'
import { useGetInventoryMonthlyActivity } from '../../../../api/dealership/inventory/analysis/useGetInventoryMonthlyActivity'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'

const columnHelper = createColumnHelper<Vehicle>()

const InventoryAnalysis = (): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const {
    data: { totalVehicles = 0, newVehicles = 0, usedVehicles = 0 } = {},
    isLoading,
  } = useGetOverallStats()

  const {
    data: breakdownByYear,
    isLoading: isLoadingBreakdownByYear,
    isPreviousData: isPreviousDataBreakdownByYear,
  } = useGetInventoryBreakdownStats({ type: 'year' })
  const {
    data: breakdownByMake,
    isLoading: isLoadingBreakdownByMake,
    isPreviousData: isPreviousDataBreakdownByMake,
  } = useGetInventoryBreakdownStats({ type: 'make' })

  const {
    data: breakdownByNewModels,
    isLoading: isLoadingBreakdownByNewModels,
    isPreviousData: isPreviousDataBreakdownByNewModels,
  } = useGetInventoryBreakdownStats({ type: 'model', filters: { vehicle_state_id: 1 } })

  const { data: inventoryAlerts } = useGetInventoryAlerts()

  const { data: inventoryAnalysis, isLoading: isLoadingAnalysis } =
    useGetInventoryAnalysis({
      filters: {
        start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
      },
    })

  const {
    data: monthlyActivity,
    isLoading: isLoadingMonthlyActivity,
    isPreviousData: isPreviousMonthlyActivityData,
  } = useGetInventoryMonthlyActivity({
    filters: {
      start_date: dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
    },
  })

  const vehiclesAddedToInventoryColumns = useMemo(
    () => [
      columnHelper.display({
        header: () => 'Vehicle',
        id: 'vehicle',
        cell: ({ row }) => (
          <Link
            to={`../vehicles/${row.original.vin_number}`}
            className={'font-bold text-primary-persian-blue'}>
            {row.original.year} {row.original.make} {row.original.model}
          </Link>
        ),
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => `$${formatNumber(Number(info.getValue()))}`,
      }),
      columnHelper.display({
        header: () => 'Date added',
        id: 'first_seen',
        cell: ({ row }) => dayjs(row.original.first_seen).format('MMM DD'),
      }),
    ],
    [],
  )

  const vehiclesRemovedFromInventoryColumns = useMemo(
    () => [
      columnHelper.display({
        header: () => 'Vehicle',
        id: 'vehicle',
        cell: ({ row }) => (
          <span>
            {row.original.year} {row.original.make} {row.original.model}
          </span>
        ),
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => `$${formatNumber(Number(info.getValue()))}`,
      }),
      columnHelper.display({
        header: () => 'Date removed',
        id: 'last_seen',
        cell: ({ row }) => dayjs(row.original.last_seen).format('MMM DD'),
      }),
    ],
    [],
  )

  const isLoaded = useMemo(() => {
    return (
      !isLoading &&
      !isLoadingAnalysis &&
      !isLoadingMonthlyActivity &&
      !isLoadingBreakdownByYear
    )
  }, [isLoading, isLoadingAnalysis, isLoadingBreakdownByYear, isLoadingMonthlyActivity])

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  useEffect(() => {
    if (isLoaded) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  })

  return (
    <div className={'flex flex-col gap-8 pb-8'} id={'content'}>
      <div className="flex flex-row justify-between items-center">
        <span className="text-md">
          This page is an analysis on your current, moved, and sold inventory.
        </span>
        <div className="print:hidden self-end">
          <Button
            disabled={!isLoaded || isDownloadingReport}
            leftSlot={<DownloadIcon className={'fill-white'} />}
            animate={false}
            onClick={async (e) => {
              e.preventDefault()
              const toastId = toast.loading('Generating report...')
              GA4.sendEvent({
                event_name: 'export_pdf',
                event_params: {
                  campaign_type: 'inventory_analysis',
                },
              })
              mtm.sendEvent('Export', 'PDF', 'inventory_analysis')
              await downloadReport(undefined, {
                async onSuccess(data) {
                  toast.success('Report generated successfully', { id: toastId })
                  const blob = await data.blob()
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `${dealership?.dealership}_inventory_insights.pdf`
                  document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                  a.click()
                  a.remove()
                },
              })
            }}>
            Export as PDF
          </Button>
        </div>
      </div>
      <div className={'flex grid grid-cols-3 gap-5'}>
        <StatsCard
          title={'Total cars in inventory'}
          content={`${formatNumber(totalVehicles)}`}
          color={'bg-palatinate-blue/30'}
        />
        <StatsCard
          title="New cars"
          content={`${formatNumber(newVehicles)}`}
          color="bg-kelly-green/30"
        />
        <StatsCard
          title="Used cars"
          content={`${formatNumber(usedVehicles)}`}
          color={'bg-dodger-blue/30'}
        />
      </div>
      <div className="flex grid grid-cols-1">
        <div className={'min-h-[300px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Vehicles in inventory last 30 days</span>
          </div>
          <div className={'h-[300px] p-2'}>
            <StackedBarChart
              data={
                inventoryAnalysis?.inventoryOverTime?.map((data) => {
                  return {
                    Date: dayjs(data.date).format('MMM D'),
                    New: data.new_vehicles,
                    Used: data.used_vehicles,
                  }
                }) ?? []
              }
              isLoading={isLoadingAnalysis}
              yKeys={['New', 'Used']}
              xKey={'Date'}
            />
          </div>
        </div>
      </div>
      <div className={'min-h-[270px] border-2 rounded-xl'}>
        <div
          className={
            'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
          }>
          <div className="flex flex-row gap-2 items-center">
            <span>Inventory Activity by Month</span>
          </div>
        </div>
        <div className={'h-[270px] p-2'}>
          <MultiLineChart
            data={
              monthlyActivity
                ?.flatMap((d) => {
                  return [
                    {
                      date: d.date,
                      type: 'Vehicles Added',
                      count: d.vehicles_added,
                    },
                    {
                      date: d.date,
                      type: 'Vehicles Removed',
                      count: d.vehicles_removed,
                    },
                  ]
                })
                ?.sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1)) ?? []
            }
            borderWidth={2}
            fill={false}
            customOptions={{
              elements: {
                line: {
                  tension: 0,
                },
                point: {
                  radius: 0,
                },
              },
              plugins: {
                legend: {
                  position: 'top',
                  align: 'end',
                },
                tooltip: {
                  mode: 'index' as const,
                  intersect: false,
                },
              },
              datalabels: {
                formatter: (value: string): string => dayjs(value).format('MMMM'),
              },
            }}
            gradient={['blue-to-purple', 'red-to-orange']}
            borderColor={'white'}
            groupKey={'type'}
            labelKey={'type'}
            yKey={'count'}
            xKey={'date'}
            isLoading={isLoadingMonthlyActivity}
            isPreviousData={isPreviousMonthlyActivityData}
            customLabel={(value) => dayjs(value).format("MMM 'YY")}
          />
        </div>
      </div>
      <div className={'flex grid grid-cols-3 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Inventory by year</span>
          </div>
          <div className={'h-[350px] p-2'}>
            <HorizontalBarChart
              data={(breakdownByYear ?? []).map((d) => {
                return {
                  label: d.year,
                  value: Number(d.count),
                }
              })}
              isLoading={isLoadingBreakdownByYear}
              isPreviousData={isPreviousDataBreakdownByYear}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Inventory by make</span>
          </div>
          <div className={'h-[350px] p-2'}>
            <HorizontalBarChart
              data={(breakdownByMake ?? []).map((d) => {
                return {
                  label: d.make,
                  value: Number(d.count),
                }
              })}
              isLoading={isLoadingBreakdownByMake}
              isPreviousData={isPreviousDataBreakdownByMake}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>New car models</span>
          </div>
          <div className={'h-[350px] p-2'}>
            <HorizontalBarChart
              data={(breakdownByNewModels ?? []).map((d) => {
                return {
                  label: d.model,
                  value: Number(d.count),
                }
              })}
              isLoading={isLoadingBreakdownByNewModels}
              isPreviousData={isPreviousDataBreakdownByNewModels}
            />
          </div>
        </div>
      </div>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic items-center'
            }>
            <span>Vehicles on lot more than 30 days</span>
            <div className={'print:hidden'}>
              <Button variant={'secondary'}>
                <Link to={t('dealership.campaigns.create', { dealership })}>
                  Market these vehicles
                </Link>
              </Button>
            </div>
          </div>
          <div className={'h-[270px]'}>
            <PolarAreaChart
              data={
                take(
                  inventoryAnalysis?.vehiclesOnLot?.map((vehicle) => {
                    return {
                      label: vehicle.vehicle_state,
                      value: vehicle.count,
                    }
                  }),
                  5,
                ) ?? []
              }
              isLoading={isLoadingAnalysis}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic items-center'
            }>
            <span>Vehicles added this week</span>
            <div className={'print:hidden'}>
              <Button variant={'secondary'}>
                <Link to={t('dealership.campaigns.create', { dealership })}>
                  Market these vehicles
                </Link>
              </Button>
            </div>
          </div>
          <div className={'h-[270px]'}>
            <PolarAreaChart
              data={
                take(
                  inventoryAnalysis?.recentlyAdded?.map((vehicle) => {
                    return {
                      label: vehicle.raw_make,
                      value: vehicle.count,
                    }
                  }),
                  5,
                ) ?? []
              }
              isLoading={isLoadingAnalysis}
            />
          </div>
        </div>
      </div>
      <div className={'flex flex-col gap-4 w-full border-2 p-4 rounded-xl'}>
        <div className={'font-bold text-sm'}>Inventory feed alerts</div>
        <div className={'flex grid grid-cols-2 gap-4'}>
          <StatsCard
            title={'Vehicles with no price'}
            content={formatNumber(inventoryAlerts?.vehicles_with_no_price ?? 0)}
          />
          <StatsCard
            title={'Vehicles with no image'}
            content={formatNumber(inventoryAlerts?.vehicles_with_no_image ?? 0)}
          />
        </div>
      </div>
      <div className={'grid grid-cols-2 gap-4'}>
        <div className={'flex flex-col gap-4 border-2 pt-5 px-5 pb-2 rounded-xl'}>
          <span className={'font-bold text-primary-xiketic text-sm'}>
            Vehicles recently added to inventory
          </span>
          <Table
            emptyLabel={'No vehicles found'}
            columns={vehiclesAddedToInventoryColumns}
            customPageSize={10}
            filters={[]}
            defaultSorting={[
              { id: 'first_seen', desc: true },
              { id: 'vin_number', desc: true },
            ]}
            fetchFn={useGetAddedInventory}
          />
        </div>
        <div className={'flex flex-col gap-4 border-2 pt-5 px-5 pb-2 rounded-xl'}>
          <span className={'font-bold text-primary-xiketic text-sm'}>
            Vehicles recently removed from inventory
          </span>
          <Table
            emptyLabel={'No vehicles found'}
            columns={vehiclesRemovedFromInventoryColumns}
            customPageSize={10}
            filters={[]}
            defaultSorting={[
              { id: 'last_seen', desc: true },
              { id: 'vin_number', desc: true },
            ]}
            fetchFn={useGetRemovedInventory}
          />
        </div>
      </div>
    </div>
  )
}

export default InventoryAnalysis
